/**
 * The address book (design 3.8): people and firms the company works with, who are not users.
 * Admins see the whole book; categories and extra fields are system-admin settings.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Contact, ContactCategory, ContactField, ContactPage, type ContactFieldKind } from "@workcore/schemas";
import { api, checkedWrite, get } from "../../api";
import { EmptyState, ErrorBox, Field, PageHeader, Panel, RowMenu, SidePanel, StatusBadge, toast } from "../../ui";
import { confirmAction } from "../../confirm";
import { HistoryLink } from "../../activity";
import { InlineText, toKey } from "../editor/shared";
import { useMe } from "../projects/Projects";

const key = (slug: string, what: string) => ["ws", slug, "contacts", what];

export function useContactSettings(slug: string) {
  const categories = useQuery({ queryKey: key(slug, "categories"), queryFn: async () => z.array(ContactCategory).parse(await get(`/v1/ws/${slug}/contact-categories`, { all: "true" })) });
  const fields = useQuery({ queryKey: key(slug, "fields"), queryFn: async () => z.array(ContactField).parse(await get(`/v1/ws/${slug}/contact-fields`, { all: "true" })) });
  return { categories: categories.data ?? [], fields: fields.data ?? [] };
}

/** Search the book (for pickers as well as the list). */
export function useContactSearch(slug: string, q: string, extra: Record<string, string> = {}) {
  return useQuery({
    queryKey: [...key(slug, "list"), q, extra],
    queryFn: async () => ContactPage.parse(await get(`/v1/ws/${slug}/contacts`, { limit: "200", ...(q ? { q } : {}), ...extra })),
  });
}

type Tab = "book" | "categories" | "fields";

export function ContactsPage() {
  const { slug = "" } = useParams();
  const me = useMe(slug).data;
  const sys = me?.isSystemAdmin ?? false;
  const [tab, setTab] = useState<Tab>("book");
  return (
    <>
      <PageHeader title="Contacts" description="People and firms you work with who do not sign in: builders, architects, electricians. Add them here, then put them on projects with a role." actions={<HistoryLink slug={slug} kind="contacts" />} />
      <nav className="tabs">
        {(
          [
            ["book", "Address book"],
            ["categories", "Categories"],
            ["fields", "Fields"],
          ] as const
        ).map(([id, label]) => (
          <button key={id} className={tab === id ? "active" : ""} onClick={() => setTab(id)}>{label}</button>
        ))}
      </nav>
      {tab === "book" && <Book slug={slug} />}
      {tab === "categories" && <Categories slug={slug} canEdit={sys} />}
      {tab === "fields" && <Fields slug={slug} canEdit={sys} />}
    </>
  );
}

function Book({ slug }: { slug: string }) {
  const q = useQueryClient();
  const { categories } = useContactSettings(slug);
  const [text, setText] = useState("");
  const [category, setCategory] = useState("");
  const [archived, setArchived] = useState(false);
  const list = useContactSearch(slug, text, { ...(category ? { category } : {}), ...(archived ? { include_archived: "true" } : {}) });
  const firms = useContactSearch(slug, "", { kind: "organisation" });
  const [editing, setEditing] = useState<Contact | "new" | null>(null);
  const refresh = () => q.invalidateQueries({ queryKey: ["ws", slug, "contacts"] });
  const archive = useMutation({
    mutationFn: ({ c, to }: { c: Contact; to: boolean }) => checkedWrite("POST", `/v1/ws/${slug}/contacts/${c.id}/${to ? "archive" : "unarchive"}`),
    onSuccess: refresh,
  });
  const items = list.data?.items ?? [];
  const firmName = (id: number | null) => (id === null ? "" : (firms.data?.items.find((f) => f.id === id)?.name ?? `#${id}`));
  const label = (k: string) => categories.find((c) => c.key === k)?.label ?? k;
  return (
    <>
      <div className="inline wrap toolbar">
        <label className="inline-field"><span>Search</span><input aria-label="Search contacts" placeholder="name or email" value={text} onChange={(e) => setText(e.target.value)} /></label>
        <label className="inline-field"><span>Category</span><select aria-label="Contact category filter" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">all</option>
          {categories.filter((c) => !c.archived).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select></label>
        <label className="check"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> show archived</label>
        <span className="spacer" />
        <button onClick={() => setEditing("new")}>New contact</button>
      </div>
      <ErrorBox error={list.error ?? archive.error} />
      {list.isSuccess && items.length === 0 ? (
        <EmptyState title={text || category ? "No contacts match" : "No contacts yet"} body="Add the firms and people you work with. Each can then be put on any number of projects, in whatever role they play there." action={<button onClick={() => setEditing("new")}>New contact</button>} />
      ) : (
        <Panel plain>
          <table className="grid">
            <thead><tr><th>Name</th><th>Firm</th><th>Categories</th><th>Email</th><th className="opt">Phone</th><th /></tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className={c.archived ? "inactive" : ""}>
                  <td>
                    <button type="button" className="link" onClick={() => setEditing(c)}>{c.name}</button>
                    {c.kind === "organisation" && <span className="muted small"> firm</span>}
                    {c.archived && <> <StatusBadge status="archived" /></>}
                  </td>
                  <td>{firmName(c.organisationId)}</td>
                  <td>{c.categories.map(label).join(", ")}</td>
                  <td>{c.email ?? ""}</td>
                  <td className="opt">{c.phone ?? ""}</td>
                  <td className="row-actions">
                    <RowMenu
                      label={`Actions for ${c.name}`}
                      items={[
                        { label: "Edit", onSelect: () => setEditing(c) },
                        c.archived
                          ? { label: "Unarchive", onSelect: () => archive.mutate({ c, to: false }) }
                          : {
                              label: "Archive…",
                              danger: true,
                              onSelect: () =>
                                void confirmAction({ title: `Archive ${c.name}?`, body: "They leave the address book and can no longer be put on projects. Nothing is deleted; you can unarchive.", action: "Archive", check: () => api("POST", `/v1/ws/${slug}/contacts/${c.id}/archive`, { dryRun: true }) }).then((ok) => ok && archive.mutate({ c, to: true })),
                            },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
      <ContactPanel slug={slug} contact={editing} firms={(firms.data?.items ?? []).filter((f) => !f.archived)} onDone={() => (setEditing(null), void refresh())} />
    </>
  );
}

function ContactPanel({ slug, contact, firms, onDone }: { slug: string; contact: Contact | "new" | null; firms: Contact[]; onDone: () => void }) {
  const { categories, fields } = useContactSettings(slug);
  const isNew = contact === "new";
  const c = contact && contact !== "new" ? contact : undefined;
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [lastKey, setLastKey] = useState<string>("");
  const k = isNew ? "new" : c ? `${c.id}:${c.version}` : "";
  if (k !== lastKey) {
    setLastKey(k);
    setForm(c ? { kind: c.kind, name: c.name, organisationId: c.organisationId, email: c.email ?? "", phone: c.phone ?? "", address: c.address ?? "", notes: c.notes, categories: c.categories, custom: c.custom } : { kind: "person", name: "", organisationId: null, email: "", phone: "", address: "", notes: "", categories: [], custom: {} });
  }
  const set = (patch: Record<string, unknown>) => setForm((f) => ({ ...f, ...patch }));
  const custom = (form.custom ?? {}) as Record<string, unknown>;
  const save = useMutation({
    mutationFn: async () => {
      const nul = (v: unknown) => (v === "" ? null : v);
      const body = { name: form.name, organisationId: form.kind === "person" ? (form.organisationId ?? null) : null, email: nul(form.email), phone: nul(form.phone), address: nul(form.address), notes: form.notes, categories: form.categories, custom: Object.fromEntries(Object.entries(custom).map(([key, v]) => [key, v === "" ? null : v])) };
      if (isNew) await checkedWrite("POST", `/v1/ws/${slug}/contacts`, { ...body, kind: form.kind, custom: Object.fromEntries(Object.entries(body.custom).filter(([, v]) => v !== null)) });
      else await checkedWrite("PATCH", `/v1/ws/${slug}/contacts/${c!.id}`, { ...body, version: c!.version });
    },
    onSuccess: () => (toast("Saved"), onDone()),
  });
  const cats = (form.categories ?? []) as string[];
  return (
    <SidePanel title={isNew ? "New contact" : (c?.name ?? "")} open={!!contact} onClose={onDone} onSubmit={() => save.mutate()} submitLabel={isNew ? "Add contact" : "Save"} busy={save.isPending} error={save.error}>
      {isNew && (
        <Field label="This is">
          <select aria-label="Contact kind" value={String(form.kind)} onChange={(e) => set({ kind: e.target.value })}>
            <option value="person">a person</option>
            <option value="organisation">a firm or organisation</option>
          </select>
        </Field>
      )}
      <Field label="Name"><input required autoFocus value={String(form.name ?? "")} onChange={(e) => set({ name: e.target.value })} /></Field>
      {form.kind === "person" && (
        <Field label="Works for" help="A firm already in the address book.">
          <select aria-label="Works for" value={String(form.organisationId ?? "")} onChange={(e) => set({ organisationId: e.target.value ? Number(e.target.value) : null })}>
            <option value="">—</option>
            {firms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </Field>
      )}
      <Field label="Email"><input type="email" value={String(form.email ?? "")} onChange={(e) => set({ email: e.target.value })} /></Field>
      <Field label="Phone"><input value={String(form.phone ?? "")} onChange={(e) => set({ phone: e.target.value })} /></Field>
      <Field label="Address"><textarea rows={3} value={String(form.address ?? "")} onChange={(e) => set({ address: e.target.value })} /></Field>
      {categories.filter((x) => !x.archived || cats.includes(x.key)).length > 0 && (
        <Field label="Categories">
          <span className="checks">
            {categories.filter((x) => !x.archived || cats.includes(x.key)).map((x) => (
              <label className="check" key={x.key}>
                <input type="checkbox" checked={cats.includes(x.key)} onChange={(e) => set({ categories: e.target.checked ? [...cats, x.key] : cats.filter((y) => y !== x.key) })} />
                {x.label}
              </label>
            ))}
          </span>
        </Field>
      )}
      {fields.filter((f) => f.status === "active").map((f) => (
        <Field key={f.key} label={f.label + (f.required ? " *" : "")}>
          <CustomInput kind={f.kind} field={f} value={custom[f.key]} onChange={(v) => set({ custom: { ...custom, [f.key]: v } })} />
        </Field>
      ))}
      <Field label="Notes" help="Markdown."><textarea rows={4} value={String(form.notes ?? "")} onChange={(e) => set({ notes: e.target.value })} /></Field>
    </SidePanel>
  );
}

function CustomInput({ kind, field, value, onChange }: { kind: ContactFieldKind; field: ContactField; value: unknown; onChange: (v: unknown) => void }) {
  const opts = (field.config.options ?? []).filter((o) => !o.archived || (Array.isArray(value) ? value.includes(o.key) : value === o.key));
  switch (kind) {
    case "boolean":
      return <input type="checkbox" aria-label={field.label} checked={value === true} onChange={(e) => onChange(e.target.checked)} />;
    case "integer":
    case "decimal":
      return <input type="number" aria-label={field.label} step={kind === "integer" ? 1 : "any"} value={value === undefined || value === null ? "" : String(value)} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />;
    case "date":
      return <input type="date" aria-label={field.label} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />;
    case "single_select":
      return (
        <select aria-label={field.label} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      );
    case "multi_select": {
      const vals = Array.isArray(value) ? (value as string[]) : [];
      return (
        <span className="checks">
          {opts.map((o) => (
            <label className="check" key={o.key}><input type="checkbox" checked={vals.includes(o.key)} onChange={(e) => onChange(e.target.checked ? [...vals, o.key] : vals.filter((v) => v !== o.key))} />{o.label}</label>
          ))}
        </span>
      );
    }
    case "rich_text":
      return <textarea rows={3} aria-label={field.label} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />;
    default:
      return <input aria-label={field.label} type={kind === "url" ? "url" : "text"} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />;
  }
}

function Categories({ slug, canEdit }: { slug: string; canEdit: boolean }) {
  const q = useQueryClient();
  const { categories } = useContactSettings(slug);
  const [label, setLabel] = useState("");
  const write = useMutation({
    mutationFn: ({ method, path, body }: { method: string; path: string; body: unknown }) => checkedWrite(method, path, body),
    onSuccess: () => toast("Saved"),
    onSettled: () => q.invalidateQueries({ queryKey: key(slug, "categories") }),
  });
  return (
    <Panel title="Categories">
      <p className="muted small">Your own trades and kinds of contact. Keys never change; archive a category you no longer use.</p>
      <table className="grid">
        <thead><tr><th>Label</th><th>Key</th><th>Status</th><th /></tr></thead>
        <tbody>
          {categories.map((c) => (
            <tr key={c.id} className={c.archived ? "inactive" : ""}>
              <td><InlineText label={`${c.label} label`} value={c.label} disabled={!canEdit} onSave={(v) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/contact-categories/${c.id}`, body: { label: v } })} /></td>
              <td><code>{c.key}</code></td>
              <td><StatusBadge status={c.archived ? "archived" : "active"} /></td>
              <td className="row-actions">{canEdit && <RowMenu label={`Actions for ${c.label}`} items={[{ label: c.archived ? "Unarchive" : "Archive", onSelect: () => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/contact-categories/${c.id}`, body: { archived: !c.archived } }) }]} />}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit && (
        <form autoComplete="off" className="inline wrap" onSubmit={(e) => { e.preventDefault(); write.mutate({ method: "POST", path: `/v1/ws/${slug}/contact-categories`, body: { key: toKey(label), label } }, { onSuccess: () => setLabel("") }); }}>
          <label className="inline-field"><span>New category</span><input aria-label="New category" required placeholder="e.g. Electrician" value={label} onChange={(e) => setLabel(e.target.value)} /></label>
          <button type="submit">Add category</button>
        </form>
      )}
      <ErrorBox error={write.error} />
    </Panel>
  );
}

const KINDS: ContactFieldKind[] = ["text", "rich_text", "integer", "decimal", "boolean", "date", "datetime", "url", "single_select", "multi_select"];

function Fields({ slug, canEdit }: { slug: string; canEdit: boolean }) {
  const q = useQueryClient();
  const { fields } = useContactSettings(slug);
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<ContactFieldKind>("text");
  const [options, setOptions] = useState("");
  const write = useMutation({
    mutationFn: ({ method, path, body }: { method: string; path: string; body: unknown }) => checkedWrite(method, path, body),
    onSuccess: () => toast("Saved"),
    onSettled: () => q.invalidateQueries({ queryKey: key(slug, "fields") }),
  });
  const select = kind === "single_select" || kind === "multi_select";
  return (
    <Panel title="Extra fields">
      <p className="muted small">Details every contact can carry beyond name, email, phone and address: a VAT number, an insurance expiry, a rating.</p>
      {fields.length > 0 && (
        <table className="grid">
          <thead><tr><th>Label</th><th>Key</th><th>Kind</th><th>Required</th><th>Status</th><th /></tr></thead>
          <tbody>
            {fields.map((f) => (
              <tr key={f.id} className={f.status === "hidden" ? "inactive" : ""}>
                <td><InlineText label={`${f.label} label`} value={f.label} disabled={!canEdit} onSave={(v) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/contact-fields/${f.id}`, body: { label: v } })} /></td>
                <td><code>{f.key}</code></td>
                <td>{f.kind.replace("_", " ")}{f.config.options ? `: ${f.config.options.filter((o) => !o.archived).map((o) => o.label).join(", ")}` : ""}</td>
                <td><input type="checkbox" aria-label={`${f.label} required`} disabled={!canEdit} checked={f.required} onChange={(e) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/contact-fields/${f.id}`, body: { required: e.target.checked } })} /></td>
                <td><StatusBadge status={f.status} /></td>
                <td className="row-actions">{canEdit && <RowMenu label={`Actions for ${f.label}`} items={[{ label: f.status === "hidden" ? "Unhide" : "Hide", onSelect: () => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/contact-fields/${f.id}`, body: { status: f.status === "hidden" ? "active" : "hidden" } }) }]} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canEdit && (
        <form
          autoComplete="off"
          className="inline wrap"
          onSubmit={(e) => {
            e.preventDefault();
            const opts = options.split(",").map((s) => s.trim()).filter(Boolean).map((l) => ({ key: toKey(l).replace(/_/g, "-"), label: l }));
            write.mutate({ method: "POST", path: `/v1/ws/${slug}/contact-fields`, body: { key: toKey(label), label, kind, config: select ? { options: opts } : {} } }, { onSuccess: () => (setLabel(""), setOptions("")) });
          }}
        >
          <label className="inline-field"><span>Label</span><input aria-label="New contact field" required placeholder="e.g. VAT number" value={label} onChange={(e) => setLabel(e.target.value)} /></label>
          <label className="inline-field"><span>Kind</span><select aria-label="New contact field kind" value={kind} onChange={(e) => setKind(e.target.value as ContactFieldKind)}>
            {KINDS.map((k) => <option key={k} value={k}>{k.replace("_", " ")}</option>)}
          </select></label>
          {select && <label className="inline-field"><span>Options</span><input aria-label="New contact field options" required placeholder="Good, Poor" value={options} onChange={(e) => setOptions(e.target.value)} /></label>}
          <button type="submit">Add field</button>
        </form>
      )}
      <ErrorBox error={write.error} />
    </Panel>
  );
}
